import React from 'react';
import { Box, Dialog, InputBase, Typography } from '@mui/material';
import { getSearch } from './api/botOpsService';
import { Snapshot } from './lib/types';
import { KIND_LABEL, PaletteItem, ServerHits, buildIndex, fromServer, parseAsk, search } from './lib/palette';
import { OVERLINE } from './lib/brand';

export const SEARCH_DEBOUNCE_MS = 250;

/**
 * ⌘K / Ctrl-K / `/`: one search over bots, items, Grant asks, threads, and (from
 * the server) recommendation history and documents, plus commands and
 * `ask <bot>`. A combobox over a listbox: ↑↓ move, Enter opens, Esc closes.
 */
export function CommandPalette({ open, onClose, snapshot, onRun }: {
  open: boolean; onClose: () => void; snapshot: Snapshot | null; onRun: (item: PaletteItem) => void;
}) {
  const [q, setQ] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [hits, setHits] = React.useState<ServerHits | null>(null);
  const [serverDown, setServerDown] = React.useState(false);
  const index = React.useMemo(() => (snapshot ? buildIndex(snapshot) : []), [snapshot]);

  React.useEffect(() => { if (open) { setQ(''); setActive(0); setHits(null); setServerDown(false); } }, [open]);

  // Recommendation history and documents are not in the browser's snapshot: ask the server, after a short pause.
  React.useEffect(() => {
    const term = q.trim();
    if (!open || term.length < 2 || parseAsk(q) !== null) { setHits(null); return undefined; }
    let cancelled = false;
    const t = window.setTimeout(() => {
      getSearch(term).then((r) => { if (!cancelled) { setHits(r.data); setServerDown(false); } }, () => { if (!cancelled) { setHits(null); setServerDown(true); } });
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [q, open]);

  const results = React.useMemo(() => {
    const local = search(index, q, snapshot || undefined);
    const extra = fromServer(hits);
    if (!extra.length) return local;
    // Server hits sit after the local matches of the same strength; a simple merge keeps the order stable.
    return local.concat(extra).slice(0, 40);
  }, [index, q, hits, snapshot]);

  React.useEffect(() => { setActive(0); }, [q]);
  const listId = 'bo-palette-list';
  const optId = (i: number) => `bo-palette-opt-${i}`;
  const choose = (i: number) => { const it = results[i]; if (it) { onClose(); onRun(it); } };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(active); }
  };

  React.useEffect(() => {
    const el = document.getElementById(optId(active));
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, results]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-label="Search and commands" PaperProps={{ sx: { alignSelf: 'flex-start', mt: '10vh' }, 'data-testid': 'palette' } as object}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <InputBase
          autoFocus
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search bots, items, recommendations, documents… or type a command  ·  ask cfo"
          inputProps={{ role: 'combobox', 'aria-label': 'Search', 'aria-expanded': true, 'aria-controls': listId, 'aria-activedescendant': results.length ? optId(active) : undefined, 'aria-autocomplete': 'list' }}
          sx={{ fontSize: 15 }}
        />
      </Box>
      <Box id={listId} role="listbox" aria-label="Results" sx={{ maxHeight: '55vh', overflowY: 'auto', py: 0.5 }}>
        {results.length === 0 && <Typography data-testid="palette-empty" variant="body2" color="text.secondary" sx={{ p: 2 }}>Nothing matches “{q.trim()}”.</Typography>}
        {results.map((r, i) => (
          <Box
            key={r.id}
            id={optId(i)}
            role="option"
            aria-selected={i === active}
            data-kind={r.kind}
            onMouseMove={() => active !== i && setActive(i)}
            onClick={() => choose(i)}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1.2, px: 2, py: 0.9, cursor: 'pointer', bgcolor: i === active ? 'action.selected' : 'transparent' }}
          >
            <Typography component="span" sx={{ ...OVERLINE, fontSize: 10.5, width: 92, flexShrink: 0 }}>{KIND_LABEL[r.kind]}</Typography>
            <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 600, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</Typography>
            {r.hint && <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{r.hint}</Typography>}
          </Box>
        ))}
      </Box>
      {serverDown && <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1 }}>Recommendation history and documents could not be searched just now.</Typography>}
    </Dialog>
  );
}
